import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FileText, ImageOff, Paperclip, X } from "lucide-react";
import { uploadFile } from "@/lib/api";
import { filesFromTransfer, transferHasFiles } from "@/lib/imageTransfer";
import { ATTACHMENT_ACCEPT, IMAGE_ACCEPT } from "@/lib/uploadAccept";
import { formatDateTime } from "@/domain/datetime";
import { cn } from "@/lib/cn";

/** Một tệp trong danh sách: đã lưu (có `id`, `url` qua đường của bản ghi) hoặc vừa tải lên (chưa có `id`, xem trước bằng `localUrl`). */
export interface DraftFile {
  id?: string;
  key: string;
  kind: "image" | "document";
  fileName: string;
  fileSize: number;
  mimeType: string;
  url?: string;
  localUrl?: string;
  addedAt?: number;
  addedBy?: string | null;
}

const IMAGE_TYPES = new Set(IMAGE_ACCEPT.split(","));
const ANY_TYPES = new Set(ATTACHMENT_ACCEPT.split(","));

const sizeLabel = (n: number) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

/**
 * Ô kéo thả NHIỀU tệp (yêu cầu v1.8 mục 4.3 bằng chứng đặt hàng, mục 2.3 ảnh yêu cầu của khách): kéo thả, dán ảnh chụp màn hình, hoặc
 * chọn tệp. Tệp tải lên R2 ngay (vào thư mục `folder`); danh sách mới được GHI vào bản ghi khi bấm Lưu. Ảnh đã lưu mở qua đường có
 * kiểm phạm vi của bản ghi, không qua đường tệp chung.
 */
export function FileDropList({
  files,
  onChange,
  folder,
  imagesOnly = false,
  readOnly = false,
  max = 30,
  compact = false,
  onBusyChange,
}: {
  files: DraftFile[];
  onChange?: (next: DraftFile[]) => void;
  folder: "pi-attachments" | "customer-requirements" | "p-process";
  imagesOnly?: boolean;
  readOnly?: boolean;
  max?: number;
  compact?: boolean;
  /** Số tệp đang tải lên — trang khoá nút Lưu khi còn tệp chưa tải xong, không thì tệp đó rơi khỏi danh sách được lưu. */
  onBusyChange?: (uploading: number) => void;
}) {
  const { t } = useTranslation();
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(0);
  const inFlight = useRef(0);
  const [hover, setHover] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  // Danh sách mới nhất cho các lần tải song song — tránh lần sau ghi đè lần trước.
  const latest = useRef(files);
  latest.current = files;
  // `onChange` của lần render MỚI NHẤT: tải ảnh mất vài giây, trong lúc đó người dùng gõ mô tả hay thêm mục; gọi hàm của lần render
  // lúc bắt đầu tải là ghi lại bản nháp cũ của cả trình sửa (mất chữ vừa gõ, mục vừa thêm hiện lại / biến mất).
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onBusyRef = useRef(onBusyChange);
  onBusyRef.current = onBusyChange;
  useEffect(() => onBusyRef.current?.(busy), [busy]);
  // Ảnh xem trước tạo bằng URL.createObjectURL — thu hồi khi rời trang.
  const previews = useRef<string[]>([]);
  useEffect(() => {
    const urls = previews.current;
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, []);

  const accept = async (list: File[]) => {
    if (readOnly || !onChangeRef.current || list.length === 0) return;
    const problems: string[] = [];
    // Tệp đang tải dở cũng chiếm chỗ — thả nhiều đợt liên tiếp không được vượt giới hạn máy chủ nhận.
    const room = max - latest.current.length - inFlight.current;
    if (list.length > room) problems.push(t("evidence.tooMany", { max }));
    const ok = list.slice(0, Math.max(0, room)).filter((f) => {
      const allowed = imagesOnly ? IMAGE_TYPES.has(f.type) : ANY_TYPES.has(f.type);
      if (!allowed) problems.push(t(imagesOnly ? "evidence.badImage" : "evidence.badType", { name: f.name }));
      return allowed;
    });
    setErrors(problems);
    if (!ok.length) return;
    inFlight.current += ok.length;
    setBusy((n) => n + ok.length);
    for (const file of ok) {
      try {
        // Bằng chứng đặt hàng và ảnh Quy trình P giữ ảnh gốc (ảnh chụp đơn / phiếu phải đọc được chữ).
        const up = await uploadFile(file, folder, { original: folder === "pi-attachments" || folder === "p-process" });
        const localUrl = up.kind === "image" ? URL.createObjectURL(file) : undefined;
        if (localUrl) previews.current.push(localUrl);
        const draft: DraftFile = { key: up.key, kind: up.kind, fileName: up.fileName, fileSize: up.fileSize, mimeType: up.mimeType, localUrl };
        latest.current = [...latest.current, draft];
        onChangeRef.current?.(latest.current);
      } catch {
        setErrors((e) => [...e, t("evidence.uploadFailed", { name: file.name })]);
      } finally {
        inFlight.current -= 1;
        setBusy((n) => n - 1);
      }
    }
  };

  const remove = (key: string) => {
    latest.current = latest.current.filter((f) => f.key !== key);
    onChangeRef.current?.(latest.current);
  };

  return (
    <div
      className="space-y-2"
      onPaste={(e) => {
        if (readOnly) return;
        const list = filesFromTransfer(e.clipboardData);
        if (list.length) {
          e.preventDefault();
          void accept(list);
        }
      }}
    >
      {!readOnly && (
        <div
          onDragOver={(e) => {
            if (!transferHasFiles(e.dataTransfer)) return;
            e.preventDefault();
            setHover(true);
          }}
          onDragLeave={() => setHover(false)}
          onDrop={(e) => {
            e.preventDefault();
            setHover(false);
            void accept(filesFromTransfer(e.dataTransfer));
          }}
          className={cn(
            "flex flex-wrap items-center justify-center gap-1.5 rounded-sm border border-dashed px-3 text-xs text-ink-soft transition-colors",
            compact ? "py-2" : "py-4",
            hover ? "border-brand bg-brand-tint/60" : "border-line-strong bg-sunken/30",
          )}
        >
          <Paperclip className="size-3.5" aria-hidden="true" />
          <span>{t(imagesOnly ? "evidence.dropImages" : "evidence.drop")}</span>
          <label htmlFor={inputId} className="cursor-pointer font-medium text-brand underline">
            {t("evidence.choose")}
          </label>
          <input
            ref={inputRef}
            id={inputId}
            type="file"
            multiple
            accept={imagesOnly ? IMAGE_ACCEPT : ATTACHMENT_ACCEPT}
            className="sr-only"
            onChange={(e) => {
              void accept(Array.from(e.target.files ?? []));
              if (inputRef.current) inputRef.current.value = "";
            }}
          />
          {busy > 0 && <span className="ml-2 text-ink-faint">{t("evidence.uploading", { count: busy })}</span>}
        </div>
      )}
      {errors.map((m, i) => (
        <p key={i} role="alert" className="text-xs text-danger">{m}</p>
      ))}
      {files.length === 0 ? (
        readOnly ? <p className="text-xs text-ink-faint">{t("evidence.empty")}</p> : null
      ) : (
        <ul className="flex flex-wrap gap-2">
          {files.map((f) => {
            const href = f.url ?? f.localUrl;
            return (
              <li key={f.key} className="group relative w-40 rounded-sm border border-line bg-surface p-1.5">
                {f.kind === "image" ? (
                  <a href={href} target="_blank" rel="noreferrer" className="block h-24 overflow-hidden rounded-xs bg-sunken" title={f.fileName}>
                    {href ? <img src={href} alt={f.fileName} className="h-full w-full object-contain" loading="lazy" /> : <ImageOff className="m-auto size-5 text-ink-faint" />}
                  </a>
                ) : (
                  <a
                    href={f.url}
                    target="_blank"
                    rel="noreferrer"
                    className={cn("flex h-24 flex-col items-center justify-center gap-1 rounded-xs bg-sunken text-ink-soft", !f.url && "pointer-events-none")}
                    title={f.fileName}
                  >
                    <FileText className="size-7" aria-hidden="true" />
                    <span className="font-mono text-2xs uppercase">{f.fileName.split(".").pop()}</span>
                  </a>
                )}
                <p className="mt-1 truncate text-xs text-ink" title={f.fileName}>{f.fileName}</p>
                <p className="text-2xs text-ink-faint">
                  {sizeLabel(f.fileSize)}
                  {f.id ? null : <span className="ml-1 text-warn">· {t("evidence.pending")}</span>}
                </p>
                {f.addedAt ? (
                  <p className="truncate text-2xs text-ink-faint" title={t("evidence.addedBy", { at: formatDateTime(f.addedAt), name: f.addedBy ?? "—" })}>
                    {formatDateTime(f.addedAt)}
                  </p>
                ) : null}
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => remove(f.key)}
                    aria-label={`${t("evidence.remove")} ${f.fileName}`}
                    title={t("evidence.remove")}
                    className="absolute right-1 top-1 grid size-6 place-items-center rounded-full bg-surface/90 text-ink-faint shadow-sm hover:text-danger"
                  >
                    <X className="size-3.5" aria-hidden="true" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** Tệp đã lưu (DTO của máy chủ) → bản nháp trong ô. */
export function savedToDraft(f: {
  id: string;
  kind?: "image" | "document";
  fileName: string;
  fileSize: number;
  mimeType: string;
  url: string;
  createdAt?: number;
  createdByName?: string | null;
}): DraftFile {
  return {
    id: f.id,
    key: f.id,
    kind: f.kind ?? (f.mimeType.startsWith("image/") ? "image" : "document"),
    fileName: f.fileName,
    fileSize: f.fileSize,
    mimeType: f.mimeType,
    url: f.url,
    addedAt: f.createdAt,
    addedBy: f.createdByName ?? null,
  };
}
